// lib/leadTabs.js
//
// Single source of truth for every lead-status tab shown on the Dashboard
// and the LeadStatusPage.
//
// Each entry maps a tab `value` → one or more lead `statuses` that belong
// to that tab. The API filter is built from `statuses` so a single GET call
// returns the right leads without any client-side re-filtering.

export const LEAD_TABS = [
  {
    value:    'new',
    label:    'New',
    statuses: ['new'],
    color:    'from-sky-500 to-sky-600',
  },
  {
    value:    'inprogress',
    label:    'In Progress',
    statuses: ['inprogress', 'open', 'contacted'],   // 'open' & 'contacted' are legacy aliases
    color:    'from-blue-500 to-blue-600',
  },
  {
    value:    'attended',
    label:    'Attended',
    statuses: ['attended', 'demo_scheduled'],         // 'demo_scheduled' is the legacy alias
    color:    'from-indigo-500 to-indigo-600',
  },
  {
    value:    'interested',
    label:    'Interested',
    statuses: ['interested', 'awaiting_decision'],    // 'awaiting_decision' is the legacy alias
    color:    'from-violet-500 to-violet-600',
  },
  {
    value:    'not_interested',
    label:    'Not Interested',
    statuses: ['not_interested'],
    color:    'from-red-500 to-red-600',
  },
  {
    value:    'enrolled_with_others',
    label:    'Enrolled with Others',
    statuses: ['enrolled_with_others'],
    color:    'from-orange-500 to-orange-600',
  },
  {
    value:    'not_responding',
    label:    'Not Responding',
    statuses: ['not_responding'],
    color:    'from-yellow-500 to-yellow-600',
  },
  {
    value:    'enrolled',
    label:    'Enrolled',
    statuses: ['enrolled', 'converted'],              // 'converted' is the legacy alias
    color:    'from-emerald-500 to-emerald-600',
  },
  {
    value:    'lost',
    label:    'Lost',
    statuses: ['lost'],
    color:    'from-gray-500 to-gray-600',
  },
];

// Build a reverse lookup:  status string → tab value
// e.g. 'demo_scheduled' → 'attended',  'new' → 'new'
const STATUS_TO_TAB = LEAD_TABS.reduce((map, tab) => {
  tab.statuses.forEach(s => { map[s] = tab.value; });
  return map;
}, {});

/**
 * Returns the tab value a lead belongs to based on its status field.
 * Falls back to 'new' for any unmapped / legacy status.
 */
export const getLeadTab = (lead) =>
  STATUS_TO_TAB[lead?.status] ?? 'new';

// Tailwind colour classes for every lead status — used in badges across the app
const STATUS_COLOR_MAP = {
  // ── new statuses ──
  new:                  'bg-sky-100 text-sky-800',
  inprogress:           'bg-blue-100 text-blue-800',
  attended:             'bg-indigo-100 text-indigo-800',
  interested:           'bg-violet-100 text-violet-800',
  not_interested:       'bg-red-100 text-red-800',
  enrolled_with_others: 'bg-orange-100 text-orange-800',
  not_responding:       'bg-yellow-100 text-yellow-800',
  enrolled:             'bg-emerald-100 text-emerald-800',
  // ── legacy statuses ──
  open:                 'bg-blue-100 text-blue-800',
  contacted:            'bg-yellow-100 text-yellow-800',
  demo_scheduled:       'bg-indigo-100 text-indigo-800',
  awaiting_decision:    'bg-purple-100 text-purple-800',
  converted:            'bg-green-100 text-green-800',
  lost:                 'bg-gray-100 text-gray-800',
};

/**
 * Returns a Tailwind badge colour class for a given lead status string.
 * Falls back to a neutral gray for unknown values.
 */
export const getLeadStatusColor = (status) =>
  STATUS_COLOR_MAP[status] ?? 'bg-gray-100 text-gray-800';

/**
 * Human-readable label for every status value.
 */
const STATUS_LABEL_MAP = {
  new:                  'New',
  inprogress:           'In Progress',
  attended:             'Attended',
  interested:           'Interested',
  not_interested:       'Not Interested',
  enrolled_with_others: 'Enrolled with Others',
  not_responding:       'Not Responding',
  enrolled:             'Enrolled',
  open:                 'Open',
  contacted:            'Contacted',
  demo_scheduled:       'Demo Scheduled',
  awaiting_decision:    'Awaiting Decision',
  converted:            'Converted',
  lost:                 'Lost',
};

export const getLeadStatusLabel = (status) =>
  STATUS_LABEL_MAP[status] ?? status;

/** Statuses where the lead pipeline is considered closed — no further actions */
export const TERMINAL_STATUSES = new Set([
  'converted', 'enrolled', 'lost', 'not_interested', 'enrolled_with_others',
]);

/**
 * Returns the tab config object for a given tab value.
 */
export const getTabConfig = (tabValue) =>
  LEAD_TABS.find(t => t.value === tabValue) ?? LEAD_TABS[0];

/**
 * Builds the ?status= query string for the GET /leads API from a tab value.
 * e.g. getStatusQueryParam('attended') → 'attended,demo_scheduled'
 */
export const getStatusQueryParam = (tabValue) => {
  const tab = getTabConfig(tabValue);
  return tab.statuses.join(',');
};